import React, { FC, useCallback, useEffect, useState } from 'react';
import { UserInfo } from '../../../common/model/dto/user-info';
import { SlotInfo, View, Views } from 'react-big-calendar';
import OvDailyDataCalendarHoc from '../molecules/OvDailyDataCalendarHoc';
import OvCalendarView from './OvCalendarView';
import styled from 'styled-components';
import breakpoints from '../../../design-system/breakpoints';
import OvDailyDataDetailsInCalendar from '../molecules/OvDailyDataDetailsInCalendar';
import { Timestamp } from 'firebase/firestore';
import DateUtils from '../../../common/utils/services/date-utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRouteUrl } from '../../../hooks/use-route-url';
import moment from 'moment';
import queryString from 'query-string';
import { DailyDataService } from '../../../services/daily-data.service';
import { JournalEntryService } from '../../../services/journal-entry.service';
import { setMonth, setYear } from 'date-fns';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DailyDataRange } from './OvUserCharts';
import OvErrorMessage from '../atoms/OvErrorMessage';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';

const CALENDAR_DAILY_DATA_LIMIT = 100;

const OvUserInfoCalendar: FC<{ selectedUser?: UserInfo }> = ({
  selectedUser,
}) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const url = useRouteUrl();

  const [defaultCalendarDate, setDefaultCalendarDate] = useState<Date>();
  const [visibleRange, setVisibleRange] = useState<Partial<DailyDataRange>>({});
  const [selectedDay, setSelectedDay] = useState<Timestamp | undefined>();

  const {
    data: dailyDataList = [],
    isFetching: isDailyDataFetching,
    error: dailyDataError,
  } = useQuery({
    queryKey: [
      'user-daily-data-calendar',
      selectedUser?.document_id,
      visibleRange.startDate?.toISOString(),
      visibleRange.endDate?.toISOString(),
    ],
    queryFn: async () => {
      const paginatedDailyData =
        await DailyDataService.loadDailyDataListByQuery(
          queryString.stringify({
            user_document_id: selectedUser?.document_id,
            day_from: visibleRange.startDate!.toISOString(),
            day_to: visibleRange.endDate!.toISOString(),
            limit: CALENDAR_DAILY_DATA_LIMIT,
          })
        );

      return paginatedDailyData.docs;
    },
    enabled:
      !!selectedUser?.document_id &&
      !!visibleRange.startDate &&
      !!visibleRange.endDate,
  });

  const createJournalEntryMutation = useMutation({
    mutationFn: (day: string) =>
      JournalEntryService.createJournalEntry({
        day,
        user_id: selectedUser?.id,
      }),
    onSuccess: (journalEntry) => {
      if (selectedUser?.document_id && journalEntry.day) {
        navigate(`/daily-data/${selectedUser.document_id}_${journalEntry.day}`);
      }
    },
  });

  const selectedDailyDataInCalendar = selectedDay
    ? dailyDataList.find(({ day }) =>
        DateUtils.areSameDays(
          selectedDay.toDate(),
          moment(day, 'YYYY-MM-DD').toDate()
        )
      )
    : undefined;

  const handleNavigationBetweenMonths = useCallback(
    (date: Date, view: View) => {
      if (view === Views.MONTH) {
        const firstVisibleDay = moment(date)
          .startOf('month')
          .subtract(20, 'days') // 7days needed because of the layout of the calendar, and another 10 day to count post ovulation days
          .toDate();
        const lastVisibleDay = moment(date)
          .endOf('month')
          .add(7, 'days')
          .toDate();

        setVisibleRange({
          startDate: firstVisibleDay,
          endDate: lastVisibleDay,
        });
      }
    },
    []
  );

  useEffect(() => {
    const visibleMonth: number = parseInt(
      queryString.parseUrl(search).query['visible-month'] as string
    );
    const visibleYear: number = parseInt(
      queryString.parseUrl(search).query['visible-year'] as string
    );

    let date = new Date();

    if (visibleMonth && visibleYear) {
      date = setMonth(date, visibleMonth - 1);
      date = setYear(date, visibleYear);
    }

    setDefaultCalendarDate(date);
    handleNavigationBetweenMonths(date, Views.MONTH);

    return function cleanup() {
      setSelectedDay(undefined);
    };
  }, [handleNavigationBetweenMonths, search]);

  const handleSlotSelect = (slotInfo: SlotInfo) => {
    setSelectedDay(Timestamp.fromDate(slotInfo.start as Date));
  };

  const createNewJournalEntry = (timestamp: Timestamp) => {
    createJournalEntryMutation.mutate(
      DateUtils.formatDate(timestamp.toDate(), 'YYYY-MM-DD')
    );
  };

  const error = dailyDataError ?? createJournalEntryMutation.error;

  return (
    <Wrapper>
      {error && <OvErrorMessage message={error.message} />}
      {(isDailyDataFetching || createJournalEntryMutation.isPending) && (
        <OvLoadingIndicator position="fixed" />
      )}
      <ContentWrapper>
        <OvCalendarView
          events={DailyDataService.mapDailyDataToCalendarEvents(dailyDataList)}
          selectedDay={selectedDay}
          style={{
            maxWidth: '100%',
            height: 600,
          }}
          onSelect={(slotInfo) => handleSlotSelect(slotInfo)}
          onNavigate={(date: Date, view: View) => {
            navigate(
              `${url}?visible-month=${DateUtils.formatDate(
                date,
                'MM'
              )}&visible-year=${DateUtils.formatDate(date, 'YYYY')}`
            );
          }}
          headerHoc={OvDailyDataCalendarHoc}
          defaultDate={defaultCalendarDate}
        />

        <OvDailyDataDetailsInCalendar
          selectedDay={selectedDay}
          dailyData={selectedDailyDataInCalendar}
          createJournalEntry={createNewJournalEntry}
          selectedUser={selectedUser}
        />
      </ContentWrapper>
    </Wrapper>
  );
};

export default OvUserInfoCalendar;

const Wrapper = styled.div``;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.sm}) {
    flex-wrap: wrap;
  }
`;
